import { Box, Button, FormControl, FormErrorMessage, Heading, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FiTool } from 'react-icons/fi';
import { Select, CreatableSelect } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';
import {
  useGetEquipmentQuery,
  useUpdateEquipmentMutation,
  useGetEquipmentCategoriesQuery,
  useAddEquipmentCategoryMutation,
  useDeleteEquipmentCategoryMutation,
  useModalitiesQuery,
} from '@/app/services/nucleus';
import { setNotificationLogMessage } from '@/features/notification/notificationSlice';
import LoaderOverlay from '../common/LoaderOverlay';
import AlertComponent from '../common/AlertComponent';
import FormRow from '@/features/user/FormRow';
import { OptionCategoryDelete } from '@/features/common/select/Option';

const EditEquipment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialRef = useRef(null);

  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState(null);
  const [modalities, setModalities] = useState([]);
  const [nameError, setNameError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [deleteErrors, setDeleteErrors] = useState({});

  const { data: equipment, isLoading: isFetching, isError: isFetchError, error: fetchError } = useGetEquipmentQuery(id);
  const { data: categoriesData } = useGetEquipmentCategoriesQuery();
  const { data: modalitiesData } = useModalitiesQuery();

  const [addEquipmentCategory, { isLoading: isCreatingCategory }] = useAddEquipmentCategoryMutation();
  const [deleteEquipmentCategory] = useDeleteEquipmentCategoryMutation();
  const [updateEquipment, { isLoading: isSubmitting, isError: isUpdateError, error: updateError, reset }] = useUpdateEquipmentMutation();

  const modalityOptions = useMemo(() => {
    return (modalitiesData?.items || []).map((m) => ({ value: m.id, label: m.short_name }));
  }, [modalitiesData?.items]);

  // Pre-populate form when equipment data arrives
  useEffect(() => {
    if (equipment) {
      setName(equipment.name ?? '');
      setBrand(equipment.brand ?? '');
      const cat = equipment.category;
      setCategory(cat && !cat.deleted_at ? { value: cat.id, label: cat.name } : null);
      setModalities((equipment.modalities || []).map((m) => ({ value: m.id, label: m.short_name })));
    }
  }, [equipment]);

  const handleCreateCategory = async (inputValue) => {
    try {
      const result = await addEquipmentCategory({ name: inputValue }).unwrap();
      setCategory({ value: result.id, label: result.name });
      setCategoryError(false);
    } catch (e) {
      console.error('Error creating category', e);
    }
  };

  const handleRequestDelete = (id) => setPendingDeleteId(id);
  const handleCancelDelete = () => setPendingDeleteId(null);

  const handleConfirmDelete = async (id) => {
    setPendingDeleteId(null);
    setDeletingIds((prev) => new Set([...prev, id]));
    try {
      await deleteEquipmentCategory(id).unwrap();
      if (category?.value === id) setCategory(null);
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      setDeleteErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (e) {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
      setDeleteErrors((prev) => ({ ...prev, [id]: e?.data?.detail ?? 'Could not delete' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;
    if (!name.trim()) { setNameError(true); hasError = true; }
    if (!category) { setCategoryError(true); hasError = true; }
    if (hasError) return;
    reset();

    updateEquipment({
      id,
      name: name.trim(),
      brand: brand.trim(),
      category_id: category.value,
      modality_ids: modalities.map((m) => m.value),
    })
      .unwrap()
      .then(() => {
        dispatch(setNotificationLogMessage({ msg: 'Equipment updated successfully', status: 'success' }));
        navigate('/equipment');
      })
      .catch((e) => console.error('Error updating equipment', e));
  };

  return (
    <VStack spacing={4} alignItems="left">
      <Box as="header">
        <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
          <HStack><FiTool /><Text>Edit Equipment</Text></HStack>
        </Heading>
      </Box>
      {isFetchError && (
        <AlertComponent status="error" title="Error fetching equipment" description={fetchError?.data?.detail ?? 'Could not load equipment'} />
      )}
      {!isFetching && (
        <Box position="relative" width={{ base: '100%', md: '70%' }}>
          <form id="edit_equipment" onSubmit={handleSubmit}>
            <VStack spacing={4} position="relative">
              <LoaderOverlay loading={isSubmitting || isCreatingCategory} />
              {isUpdateError && (
                <AlertComponent status="error" title="Error updating equipment" description={updateError?.data?.detail ?? 'An error occurred'} />
              )}
              <FormControl isInvalid={nameError} aria-label="name-control">
                <FormRow id="name" label="Name" templateColumns="110px auto" gap={0} width="100%">
                  <Input ref={initialRef} id="name" focusBorderColor="brand.700" value={name} onChange={(e) => { setName(e.target.value); setNameError(false); }} />
                </FormRow>
                <FormErrorMessage ml="110px">Name is required.</FormErrorMessage>
              </FormControl>
              <FormControl aria-label="brand-control">
                <FormRow id="brand" label="Brand" templateColumns="110px auto" gap={0} width="100%">
                  <Input id="brand" focusBorderColor="brand.700" value={brand} onChange={(e) => setBrand(e.target.value)} />
                </FormRow>
              </FormControl>
              <FormControl isInvalid={categoryError} aria-label="category-control">
                <FormRow id="category" label="Category" templateColumns="110px auto" gap={0} width="100%">
                  <CreatableSelect
                    id="category" aria-label="category"
                    chakraStyles={chakraStyles} focusBorderColor="brand.700"
                    options={categoriesData || []}
                    value={category}
                    onChange={(opt) => { setCategory(opt); setCategoryError(false); }}
                    onCreateOption={handleCreateCategory}
                    onMenuClose={handleCancelDelete}
                    isClearable isSearchable
                    placeholder="Select or create a category"
                    components={{ Option: OptionCategoryDelete }}
                    onRequestDelete={handleRequestDelete}
                    onConfirmDelete={handleConfirmDelete}
                    onCancelDelete={handleCancelDelete}
                    pendingDeleteId={pendingDeleteId}
                    deletingIds={deletingIds}
                    deleteErrors={deleteErrors}
                  />
                </FormRow>
                <FormErrorMessage ml="110px">Category is required.</FormErrorMessage>
              </FormControl>
              <FormControl aria-label="modalities-control">
                <FormRow id="modalities" label="Modalities" templateColumns="110px auto" gap={0} width="100%">
                  <Select
                    id="modalities" aria-label="modalities"
                    chakraStyles={chakraStyles} focusBorderColor="brand.700"
                    options={modalityOptions}
                    value={modalities}
                    onChange={(opts) => setModalities(opts || [])}
                    isMulti isClearable isSearchable colorScheme="blue"
                    placeholder="Select modalities"
                  />
                </FormRow>
              </FormControl>
              <HStack width="100%" py={3} justifyContent="end">
                <Button onClick={() => navigate('/equipment')}>Cancel</Button>
                <Button type="submit" variant="purple">Save</Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      )}
    </VStack>
  );
};

export default EditEquipment;
